/**
 * Four sliders is a small question to ask somebody who has not yet decided
 * what they are looking for.
 *
 * A preset is a starting position, not an answer: it moves the weights and
 * nothing else, so the very next thing a reader does is drag one of them and
 * make it theirs. That is also why a preset is never sticky -- the chip stops
 * being marked the moment the weights no longer match it, because at that point
 * the map is the reader's question and not the preset's.
 *
 * A preset names the categories it cares about and leaves the rest out; a
 * category with no entry takes the starting weight the manifest gives it. So a
 * fifth category appearing in `propmap/prod.py` does not silently drop to zero
 * in every scenario here, and the table below stays a statement about the
 * scenario rather than a copy of the layer list.
 */

import type { Manifest } from "./manifest.ts";

export interface Preset {
  name: string;
  title: string;
  /** what a chip means, for the control's tooltip */
  means: string;
  /** weight per layer name, on the manifest's slider scale */
  weights: Record<string, number>;
  /** the way back rather than a scenario, which is why it sits on its own row */
  reset?: boolean;
}

export const PRESETS: readonly Preset[] = [
  {
    name: "balanced",
    title: "Balanced",
    means: "Every category at its starting weight",
    // Empty on purpose: every category falls back to the manifest's own
    // starting weight, so this chip is the way back to where the map opened.
    weights: {},
    reset: true,
  },
  {
    name: "family",
    title: "Family",
    means: "Schools and safety first, with room to compromise on price",
    weights: { price: 40, crime: 75, transport: 45, schools: 90 },
  },
  {
    name: "student",
    title: "Student",
    means: "Cheap and well connected; schools do not matter",
    weights: { price: 90, crime: 40, transport: 85, schools: 0 },
  },
  {
    name: "commuter",
    title: "Commuter",
    means: "Getting to work, before anything else",
    weights: { price: 55, crime: 50, transport: 95, schools: 10 },
  },
  {
    name: "quiet",
    title: "Quiet life",
    means: "Safety above all, and no interest in schools",
    weights: { price: 50, crime: 90, transport: 40, schools: 0 },
  },
];

/** A preset as a full set of weights, filling in what it does not name. */
export function weightsOf(preset: Preset, manifest: Manifest): Record<string, number> {
  const weights: Record<string, number> = {};
  for (const layer of manifest.layers) {
    weights[layer.name] = preset.weights[layer.name] ?? layer.start_weight;
  }
  return weights;
}

/**
 * The preset the current weights are sitting on, or null once a reader has
 * moved a slider away from all of them.
 */
export function matching(weights: Record<string, number>,
                         manifest: Manifest): Preset | null {
  for (const preset of PRESETS) {
    const want = weightsOf(preset, manifest);
    if (manifest.layers.every((l) => Math.round(weights[l.name] ?? 0) === Math.round(want[l.name]!))) {
      return preset;
    }
  }
  return null;
}
